import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useTrialStatus } from '../../contexts/TrialStatusContext';

/**
 * Pre-expiry trial banner. Plan-TrialExpiration.md §C Phase 2.4.
 *
 *  - Shows ONLY when daysRemaining ∈ [1, 3] AND the workspace is
 *    actively trialing (i.e. not yet expired). The blocker modal
 *    handles the expired case; this banner is the gentle pre-warning.
 *  - Auto-hides on `/billing` so the user isn't double-nagged while
 *    they're already on the upgrade page.
 *  - Renders as a soft amber bar across the main content area — soft
 *    enough to not block work, loud enough to be noticed. Mounted
 *    above page content (below the header) in Layout.tsx.
 */
const TrialBanner: React.FC = () => {
  const { daysRemaining, isExpired } = useTrialStatus();
  const location = useLocation();

  // Hide on the billing page — already there, no need to nag.
  if (location.pathname === '/billing') return null;

  // Don't fire alongside the blocker modal.
  if (isExpired) return null;

  // Fire only at the 3d / 2d / 1d thresholds. (Plan §C 2.4.)
  if (daysRemaining === null || daysRemaining < 1 || daysRemaining > 3) return null;

  const dayLabel = daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;

  return (
    <div
      role="status"
      className="bg-amber-100 dark:bg-amber-900/40 border-b border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100 px-4 py-2.5"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-start sm:items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 mt-0.5 sm:mt-0" aria-hidden="true" />
          <p className="text-sm leading-snug">
            <span className="font-semibold">{dayLabel} left in your trial.</span>{' '}
            Choose a plan to keep your workspace active.
          </p>
        </div>
        <Link
          to="/billing"
          className="inline-flex items-center justify-center text-xs sm:text-sm font-semibold underline underline-offset-2 hover:no-underline shrink-0"
        >
          Choose a plan →
        </Link>
      </div>
    </div>
  );
};

export default TrialBanner;
